import { useState, useMemo, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import useMediaQuery from '@mui/material/useMediaQuery';
import EditIcon from '@mui/icons-material/EditRounded';
import ActionIconButton from '@components/ActionIconButton';
import { useTranslation, useChannels } from '@hooks';
import usePermissions from '@hooks/usePermissions';
import { contactsActions } from '@actions';
import { ModalType } from '@constants/modal';
import DeleteIcon from '@mui/icons-material/DeleteRounded';
import { ConversationIcon, MergeContactsIcon } from 'assets/images/icons';
import Info from './Info';
import Activity from './Activity';
import { RadioButtonsWrapper, RadioButtons, StyledIconButton } from './styles';

const Tab = {
    Information: 0,
    Activity: 1,
};

const ContactInfo = ({ contact, resizable, setActiveConversation = null }) => {
    const [tab, setTab] = useState(Tab.Information);

    const companies = useSelector(state => state.contacts?.locations || []);

    const { channels, getAccessibleChannelIds, canInitiateConversation } = useChannels();
    const { hasAccess, hasFullAccess } = usePermissions();
    const canManage = hasFullAccess('contacts');
    const isSmallScreen = useMediaQuery('@media (min-width: 1025px) and (max-width: 1280px)');
    const dispatch = useDispatch();
    const { t } = useTranslation();

    useEffect(() => {
        if (!companies.length) {
            dispatch(contactsActions.getLocations());
        }
    }, [dispatch]);

    const onOpenDeleteModal = () => dispatch(contactsActions.setContactDeleteModal(true));

    const onOpenMergeModal = () => dispatch(contactsActions.setContactMergeModal(true));

    const onOpenEditModal = () => dispatch(contactsActions.setContactEditModal({ open: true, type: ModalType.Edit }));

    const onOpenInitConversation = () => {
        dispatch(
            contactsActions.setContactInitConversationModal({
                open: true,
                locations: contact?.companies,
                contactId: contact?.id,
                ctx: contact,
            }),
        );
    };

    const availableChannels = useMemo(() => {
        if (!contact) return [];
        return getAccessibleChannelIds(canInitiateConversation);
    }, [contact, channels]);

    const isDisabled = !contact;

    return (
        <>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                {hasAccess('messaging') && (
                    <ActionIconButton
                        title={t('InitConversation.newConversation')}
                        component={StyledIconButton}
                        disabled={isDisabled || availableChannels.length === 0}
                        onClick={onOpenInitConversation}
                    >
                        <ConversationIcon />
                    </ActionIconButton>
                )}
                {canManage && (
                    <>
                        <ActionIconButton
                            title={t('common.edit')}
                            component={StyledIconButton}
                            onClick={onOpenEditModal}
                            disabled={isDisabled}
                        >
                            <EditIcon viewBox="0 0 22 22" />
                        </ActionIconButton>
                        <ActionIconButton
                            title={t('buttons.merge')}
                            component={StyledIconButton}
                            onClick={onOpenMergeModal}
                            disabled={isDisabled}
                        >
                            <MergeContactsIcon />
                        </ActionIconButton>
                        <ActionIconButton
                            title={t('common.delete')}
                            component={StyledIconButton}
                            onClick={onOpenDeleteModal}
                            disabled={isDisabled}
                        >
                            <DeleteIcon />
                        </ActionIconButton>
                    </>
                )}
            </div>
            <div style={{ height: '1px', backgroundColor: 'var(--divider)', margin: '16px 0' }} />
            <RadioButtonsWrapper>
                <RadioButtons
                    value={tab}
                    onChange={setTab}
                    options={[
                        { value: Tab.Information, label: t('common.information') },
                        { value: Tab.Activity, label: t('common.activity') },
                    ]}
                />
            </RadioButtonsWrapper>
            {tab === Tab.Information && (
                <Info contact={contact} isSmallScreen={isSmallScreen} resizable={resizable} companies={companies} />
            )}
            {tab === Tab.Activity && (
                <Activity
                    contact={contact}
                    isSmallScreen={isSmallScreen}
                    resizable={resizable}
                    setActiveConversation={setActiveConversation}
                />
            )}
        </>
    );
};

export default ContactInfo;
